import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { UploadCloud, FileJson, Check, AlertTriangle, Loader2, Users as UsersIcon, ShieldCheck, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const SAMPLE_JSON = `[
  {
    "email": "user1@example.com",
    "password_hash": "$2b$12$abcdefghijklmnopqrstuv1234567890ABCDEFGHIJKLMNOPQRSTU",
    "name": "Rahul Kumar",
    "phone": "+919876543210",
    "role": "customer",
    "district": "Thrissur"
  },
  {
    "email": "cleaner1@example.com",
    "password_hash": "$2b$12$abcdefghijklmnopqrstuv1234567890ABCDEFGHIJKLMNOPQRSTV",
    "name": "Ravi Menon",
    "role": "cleaner",
    "district": "Ernakulam"
  }
]`;

const BCRYPT_RE = /^\$2[aby]\$(0[4-9]|1[0-9]|2[0-9]|3[01])\$[./A-Za-z0-9]{53}$/;

const parseCsv = (text) => {
  const rows = text.trim().split(/\r?\n/);
  if (rows.length < 2) throw new Error('CSV must have a header row and at least one data row');
  const headers = rows[0].split(',').map((h) => h.trim().toLowerCase());
  const required = ['email', 'password_hash'];
  for (const r of required) if (!headers.includes(r)) throw new Error(`CSV header must include "${r}"`);
  return rows.slice(1).filter(Boolean).map((line, idx) => {
    const cells = line.split(',').map((c) => c.trim());
    const obj = {};
    headers.forEach((h, i) => { if (cells[i] !== undefined) obj[h] = cells[i]; });
    if (!obj.email || !obj.password_hash) throw new Error(`Row ${idx + 2}: missing email or password_hash`);
    return obj;
  });
};

const AdminImportUsers = () => {
  const [mode, setMode] = useState('json');
  const [raw, setRaw] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [parseError, setParseError] = useState(null);

  const preview = useMemo(() => {
    if (!raw.trim()) return { rows: [], invalid: 0 };
    try {
      const rows = mode === 'json' ? JSON.parse(raw) : parseCsv(raw);
      if (!Array.isArray(rows)) throw new Error('Payload must be a JSON array');
      const invalid = rows.filter((r) => !r?.email || !BCRYPT_RE.test(r?.password_hash || '')).length;
      setParseError(null);
      return { rows, invalid };
    } catch (e) {
      setParseError(e.message || 'Parse error');
      return { rows: [], invalid: 0 };
    }
  }, [raw, mode]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setRaw(String(reader.result || ''));
    reader.readAsText(f);
  };

  const runImport = async () => {
    if (!preview.rows.length) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/import-users`,
        { users: preview.rows, overwrite_existing: overwrite }, authCfg());
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const clearForm = () => { setRaw(''); setResult(null); setParseError(null); };

  return (
    <AdminLayout title="Portable Hash Import">
      <div className="max-w-4xl space-y-6">
        {/* Security banner */}
        <div data-testid="import-security-banner" className="bg-[#E8F5E9] border border-[#2E7D32] rounded-xl p-4 flex gap-3">
          <ShieldCheck className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-neutral-800 space-y-1">
            <p className="font-bold text-[#1B5E20]">Zero-plaintext migration</p>
            <p>
              This tool imports users using their existing <span className="font-mono">bcrypt</span> hash (from the mobile app).
              The web ERP never sees the plaintext password. Users log in immediately with the same password they use on mobile.
            </p>
            <p>Hashes must be bcrypt modular-crypt format: <span className="font-mono">$2a$</span> / <span className="font-mono">$2b$</span> / <span className="font-mono">$2y$</span> with cost 04–31.</p>
          </div>
        </div>

        {/* Mode picker */}
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <UploadCloud className="w-5 h-5 text-[#2E7D32]" />
            <h2 className="font-extrabold text-neutral-900">1. Provide the user list</h2>
          </div>

          <div className="flex gap-2 mb-4">
            <button data-testid="import-mode-json" onClick={() => setMode('json')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${mode === 'json' ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-neutral-700 border-neutral-200'}`}>
              JSON
            </button>
            <button data-testid="import-mode-csv" onClick={() => setMode('csv')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${mode === 'csv' ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-neutral-700 border-neutral-200'}`}>
              CSV
            </button>
            <label className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-neutral-200 text-xs font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-50">
              <FileJson className="w-3.5 h-3.5" /> Upload file
              <input data-testid="import-file-input" type="file" accept=".json,.csv,.txt" onChange={onFile} className="hidden" />
            </label>
          </div>

          <textarea
            data-testid="import-raw-input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={mode === 'json' ? SAMPLE_JSON : 'email,password_hash,name,role,district\nuser@x.com,$2b$12$..........,Rahul,customer,Thrissur'}
            className="w-full h-64 px-3 py-2 rounded-lg border border-neutral-200 font-mono text-[11px] focus:outline-none focus:border-[#2E7D32]"
          />

          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input data-testid="import-overwrite-toggle" type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="rounded" />
              <span className="text-neutral-700">Overwrite existing users (matches by email)</span>
            </label>
            <button onClick={clearForm} className="ml-auto inline-flex items-center gap-1 text-neutral-500 hover:text-neutral-900">
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          </div>

          {parseError && (
            <div data-testid="import-parse-error" className="mt-3 bg-red-50 border border-red-200 text-red-800 text-xs rounded-lg px-3 py-2 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{parseError}</span>
            </div>
          )}
        </div>

        {/* Preview */}
        {preview.rows.length > 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <UsersIcon className="w-5 h-5 text-[#7B1FA2]" />
              <h2 className="font-extrabold text-neutral-900">2. Preview ({preview.rows.length} row{preview.rows.length !== 1 ? 's' : ''})</h2>
              {preview.invalid > 0 && (
                <span className="ml-auto text-xs bg-orange-50 text-orange-800 border border-orange-200 rounded-full px-2 py-0.5 font-semibold">
                  {preview.invalid} row{preview.invalid !== 1 ? 's' : ''} will be skipped (bad hash / missing email)
                </span>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-neutral-200">
              <table data-testid="import-preview-table" className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-600 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Email</th>
                    <th className="text-left px-3 py-2 font-semibold">Role</th>
                    <th className="text-left px-3 py-2 font-semibold">District</th>
                    <th className="text-left px-3 py-2 font-semibold">Hash</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 100).map((r, i) => {
                    const valid = r.email && BCRYPT_RE.test(r.password_hash || '');
                    return (
                      <tr key={i} className="border-t border-neutral-100">
                        <td className="px-3 py-1.5 font-mono">{r.email || '—'}</td>
                        <td className="px-3 py-1.5">{r.role || 'customer'}</td>
                        <td className="px-3 py-1.5">{r.district || '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-neutral-400">{(r.password_hash || '').slice(0, 15)}…</td>
                        <td className="px-3 py-1.5">
                          {valid
                            ? <span className="text-[#2E7D32] font-semibold">✓ valid</span>
                            : <span className="text-red-600 font-semibold">✗ invalid</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {preview.rows.length > 100 && <p className="text-center text-[11px] text-neutral-500 py-2">(showing first 100 rows)</p>}
            </div>

            <button
              data-testid="import-submit"
              onClick={runImport}
              disabled={busy || preview.rows.length === preview.invalid}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-60 text-white text-sm font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {busy ? 'Importing…' : `Import ${preview.rows.length - preview.invalid} user${preview.rows.length - preview.invalid !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div data-testid="import-result" className={`rounded-xl border p-5 ${result.ok ? 'bg-[#E8F5E9] border-[#2E7D32]' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              {result.ok
                ? <Check className="w-5 h-5 text-[#2E7D32]" />
                : <AlertTriangle className="w-5 h-5 text-red-600" />}
              <h2 className="font-extrabold text-neutral-900">
                {result.ok ? 'Import complete' : 'Import failed'}
              </h2>
            </div>
            {result.ok ? (
              <div className="text-xs text-neutral-800 space-y-1">
                <p><span className="font-semibold">Inserted:</span> {result.inserted}</p>
                <p><span className="font-semibold">Updated:</span> {result.updated}</p>
                <p><span className="font-semibold">Skipped:</span> {result.skipped} <span className="text-neutral-500">(already exist; enable "Overwrite" to replace)</span></p>
                <p><span className="font-semibold">Errors:</span> {result.errors?.length || 0}</p>
                {result.errors?.length > 0 && (
                  <div className="mt-2 bg-white border border-neutral-200 rounded-lg p-2 max-h-48 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <p key={i} className="font-mono text-[11px] text-red-700">{e.email} — {e.error}</p>
                    ))}
                  </div>
                )}
                <p className="mt-3 text-neutral-700">Users can now log in via <span className="font-mono">POST /api/auth/login</span> with the same password they use on the mobile app.</p>
              </div>
            ) : (
              <p className="text-xs text-red-800 font-mono">{result.error}</p>
            )}
          </div>
        )}

        {/* Format help */}
        <details className="bg-white rounded-xl border border-neutral-200 p-5">
          <summary className="cursor-pointer font-bold text-neutral-900 text-sm">Expected format</summary>
          <div className="mt-3 space-y-3 text-xs text-neutral-700">
            <div>
              <p className="font-semibold mb-1">JSON (recommended):</p>
              <pre className="bg-neutral-900 text-neutral-100 rounded-lg p-3 overflow-x-auto text-[11px]">{SAMPLE_JSON}</pre>
            </div>
            <div>
              <p className="font-semibold mb-1">CSV:</p>
              <pre className="bg-neutral-900 text-neutral-100 rounded-lg p-3 overflow-x-auto text-[11px]">{`email,password_hash,name,phone,role,district
user1@example.com,$2b$12$..............................................,Rahul Kumar,+919876543210,customer,Thrissur
cleaner1@example.com,$2b$12$..............................................,Ravi Menon,,cleaner,Ernakulam`}</pre>
            </div>
            <p><b>Required fields:</b> <code>email</code>, <code>password_hash</code>.</p>
            <p><b>Optional fields:</b> <code>name</code>, <code>phone</code>, <code>role</code> (customer|cleaner|staff|franchise_admin|chief_accountant — defaults to customer), <code>district</code>, <code>state</code>, <code>country</code>, <code>franchise_id</code>. If <code>franchise_id</code> is omitted but <code>district</code> is provided, we derive it (e.g., Thrissur → F_IN_KL_THR).</p>
          </div>
        </details>
      </div>
    </AdminLayout>
  );
};

export default AdminImportUsers;
